<template>
  <div
    :class="
      'base-create ' +
      (isDisabledComponent ? 'create-study-set-com-disable' : '')
    "
  >
    <div class="base-create-body">
      <el-form
        :rules="rules"
        :disabled="isDisabledComponent"
        :model="studySetDetails"
        class="create-edit-form"
        ref="firstForm"
        hide-required-asterisk
      >
        <div class="study-set-title">
          <el-tooltip
            class="item"
            effect="dark"
            :content="$t('common.rename')"
            placement="bottom"
          >
            <h1
              class="blue-color"
              id="study-set-name"
              v-show="!editStudySetName"
              @click="onEditName"
            >
              {{ studySetDetails.name }}
            </h1>
          </el-tooltip>

          <elsa-input
            v-show="editStudySetName"
            v-model="studySetDetails.name"
            v-on:blur="onBlurEditName"
            :placeholder="$t('study_sets.form.name_your_study_set')"
            name="name"
            ref="name"
          />
        </div>
        <el-row class="form-row">
          <el-col :md="12" :xs="24">
            <div class="pick-shareable-to">
              <elsa-select
                popper-class="emails-list-popover"
                name="shareable_to"
                :information="shareableTooltip"
                :label="$t('study_sets.form.shareable_to')"
                :options="shareableOptionsWithI18n"
                :placeholder="$t('study_sets.form.study_set_name')"
                @change="changeShareable"
                v-model.lazy="studySetDetails.shareable"
                :no-data-text="$t('study_sets.form.no_data_text')"
              />
            </div>
          </el-col>
          <el-col :md="12" :xs="24">
            <div class="pick-category">
              <elsa-select
                popper-class="emails-list-popover"
                name="category"
                :displayCount="true"
                :label="$t('study_sets.form.category')"
                :options="this.categoriesOptions"
                :placeholder="$t('study_sets.form.study_set_name')"
                @change="changeCategory"
                v-model.lazy="studySetDetails.category"
                :no-data-text="$t('study_sets.form.no_data_text')"
              />
            </div>
          </el-col>
          <elsa-tags-input
            :information="$t('information.study_set_input_items')"
            :maxTags="50"
            :displayCount="true"
            displayCountPosition="bottom"
            :label="$t('study_sets.form.input_phrases')"
            :placeholder="$t('study_sets.form.type_in_phrases_press_enter_to_seperate')"
            :maxTag="50"
            :validations="[]"
            :separators="[';', '\n', '\r']"
            :addOnkey="[13, ';']"
            :allowEditTags="true"
            :currentTags="studySetDetails.phrases"
            @updatedTags="setTags"
            name="phrases"
            ref="tags"
          />
        </el-row>
      </el-form>
    </div>
  </div>
</template>

<script>
import ElsaInput from "@/components/common/Input";
import ElsaTagsInput from "@/components/common/TagsInput";
import { handleFormValidation } from "../../../helpers/formValidationHelper";
import { mapGetters, mapActions } from "vuex";
import ElsaSelect from "@/components/common/Select";
import { path } from "ramda";
import {
  DefaultShareableOptions,
  DefaultCategoryOptions,
} from "@/constants/studySets";

export default {
  props: {
    currentStep: {
      type: Number,
      default: 1,
    },
    componentStep: {
      type: Number,
      default: 1,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    tertiaryFunction: {
      type: Function,
      default: null,
    },
    organizationType: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      editStudySetName: false,
      form: {
        name: path(["studySetDetails", "name"])(this),
        phrases: path(["studySetDetails", "phrases"])(this),
        shareable: path(["studySetDetails", "shareable"])(this),
        category: path(["studySetDetails", "category"])(this),
      },
      rules: {
        name: {
          required: true,
          type: "string",
          message: this.$t("study_sets.errors.please_provide_name"),
        },
        shareable: {
          required: true,
          message: this.$t("study_sets.errors.please_provide_shareable_to"),
          trigger: "change",
        },
        category: {
          required: true,
          message: this.$t("study_sets.errors.please_provide_category"),
          trigger: "change",
        },
      },
      shareableOptions: DefaultShareableOptions,
      categoriesOptions: DefaultCategoryOptions,
    };
  },
  computed: {
    ...mapGetters({
      studySet: "studysets/getBuildingStudySet",
      currentOrganizationId: "organization/currentOrganizationId",
      categories: "studysets/getStudySetCategories",
    }),
    studySetDetails() {
      return this.studySet;
    },
    modalTitle() {
      return this.currentStep === -1
        ? this.$t(
            this.$i18nLabelsHelper(this.organizationType, "group_details")
          )
        : this.$t(
            this.$i18nLabelsHelper(this.organizationType, "step_group_details")
          );
    },
    isDisabledComponent() {
      return this.currentStep > this.componentStep && this.editMode == false;
    },
    shareableOptionsWithI18n() {
      let shareableOptionsFilter = this.shareableOptions;
      if (!this.hasChildOrg)
        shareableOptionsFilter = this.shareableOptions.filter((x) => x.value !== 'branch')
      return shareableOptionsFilter.map((x) => {
        return { ...x, label: this.$t(this.$i18nLabelsHelper(this.organizationType, `study_sets.${x.label}`)) }
      })
    },
    shareableTooltip() {
      const tooltipClass = this.$t(this.$i18nLabelsHelper(this.organizationType, 'content_shareable.tooltip_shareable_to_my_class'));
      const tooltioOrg = this.$t('common.content_shareable.tooltip_shareable_to_this_org');
      let tooltip = `${tooltipClass} \n\n${tooltioOrg}`;
      if (this.hasChildOrg)
        tooltip = `${tooltip} \n\n${this.$t('common.content_shareable.tooltip_shareable_to_branch')}`;
      // hide for now
      // const tooltipPublic = this.$t('common.content_shareable.tooltip_shareable_to_public')
      return tooltip;
    },
  },
  components: {
    ElsaInput,
    ElsaSelect,
    ElsaTagsInput,
  },
  methods: {
    ...mapActions({
      updateBuildingStudySet: "studysets/updateBuildingStudySet",
    }),
    handleContinueClick() {
      handleFormValidation(
        this.$refs.firstForm,
        this.validFormCallback,
        () => {}
      );
    },

    validFormCallback() {
      this.$emit("next-step", this.currentStep);
    },
    handleCancelClick() {
      return this.$router.push({
        name: "classes",
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    changeCategory(category) {
      this.updateBuildingStudySet({
        category: category,
      });
    },
    changeShareable(shareable) {
      this.updateBuildingStudySet({
        shareable,
      });
    },
    setTags(newPhrases) {
      this.updateBuildingStudySet({
        phrases: newPhrases,
      });
    },
    onEditName() {
      this.editStudySetName = true;
      this.$refs.name.setFocus();
    },
    onBlurEditName() {
      this.editStudySetName = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.el-form {
  padding-bottom: 0px !important;
}

.form-row {
  margin-top: 20px;
}
.study-set-title {
  margin-bottom: 24px;
  h1 {
    font-weight: 700;
    font-size: 20px;
    line-height: 45px;
  }
}

.pick-category {
  font-weight: 600;
  font-size: 14px;
  color: #478dff;
  flex: 1;
  padding-left: 12px;
}

.pick-shareable-to {
  font-weight: 600;
  font-size: 14px;
  color: #478dff;
  flex: 1;
  padding-right: 12px;
}
.create-edit-form .form-row .el-form-item.elsa-input:not(:last-of-type) {
  margin-right: 0;
}

.input-phrases {
  width: 100%;
  height: 160px;
}

#study-set-name {
  border: 1px solid transparent;
  border-radius: 8px;
  margin-left: -8px;
  margin-right: -8px;
  padding-left: 8px;
  padding-right: 8px;
  &:hover {
    border-color: rgb(229, 229, 229);
  }
}

::v-deep .elsa-section {
  background-color: #f2f7fa;
}
::v-deep .el-date-editor.el-input {
  width: 200px;
}
::v-deep .elsa-input .el-input .el-input__inner {
  width: 100%;
}

::v-deep .elsa-tags-input {
  min-height: 190px;
}
::v-deep .ti-tag {
  background: #e9e9ec !important;
  border-radius: 4px !important;
}
</style>
